import queryString from 'query-string'
import { useMemo, useState } from 'react'
import { Box } from 'shared/components/Box/Box'
import { InlineSpinner } from 'shared/components/Spinner'
import { TextV2 } from 'shared/components/Text/TextV2'
import { TYPOGRAPHY } from 'shared/theme/typographyV2'

import { isConsole } from '../../config/config'
import { useOnboardingChecklist } from '../../hooks/query/useOnboardingChecklist'
import {
  OnboardingChecklist,
  OnboardingChecklistName,
  shouldShowOnboardingChecklist,
} from '../../models/onboarding'
import { Team } from '../../models/team'
import { User } from '../../models/user'
import { FlexBox } from '../Base/FlexBox'
import { Task, TaskList, TaskStatusEnum } from '../TaskList/TaskList'

interface OnboardingTaskListProps {
  user: User
  team: Team
  isCurrentUser?: boolean
  parseTaskList: (
    checklist: readonly OnboardingChecklist[],
    user: User,
    team: Team,
    isApplicationChecklist?: boolean
  ) => ReadonlyArray<Task>
  children?: (
    tasks: ReadonlyArray<Task>,
    applicationTasks: ReadonlyArray<Task>,
    roadPreparationTasks: ReadonlyArray<Task>
  ) => React.ReactNode
  renderApplicationSummary?: () => JSX.Element
  renderWelcomeBack?: () => JSX.Element
}

export const contractorPaymentsItems = [
  OnboardingChecklistName.CONTRACTOR_PAYMENTS_PROFILE,
  OnboardingChecklistName.PAYROLL_BANK_ACCOUNT,
  OnboardingChecklistName.ADD_CONTRACTOR,
  OnboardingChecklistName.CONTRACTOR_PERSONAL_PROFILE,
  OnboardingChecklistName.CONTRACTOR_PERSONAL_DIRECT_DEPOSIT,
  OnboardingChecklistName.CONTRACTOR_PERSONAL_DEBIT_CARD,
]

const NON_REQUIRED_TASKS: OnboardingChecklistName[] = []

export function OnboardingTaskList({
  user,
  team,
  isCurrentUser,
  parseTaskList,
  children,
  renderApplicationSummary,
  renderWelcomeBack,
}: OnboardingTaskListProps) {
  const userId = user.id
  const isCreditOnly = team.isCreditOnly
  const queryParams = queryString.parse(window.location.search)
  const skipWelcomeBack = !!queryParams.skip_welcome_back
  const skipApplicationSummary = !!queryParams.skip_application_summary
  const forceShowOnboardingTasks = Boolean(queryString.parse(window.location.search).onboarding)
  const [showAllItems, setShowAllItems] = useState(Boolean(queryString.parse(window.location.search).all))
  const { checklist, roadChecklist, allChecklistItems, onboardingStatus, isLoading } = useOnboardingChecklist(
    userId,
    {
      refetchOnMount: true,
      refetchInterval: 5000,
    }
  )
  const filteredChecklist = useMemo(() => {
    return checklist?.filter((c) => !contractorPaymentsItems.includes(c.name) || showAllItems)
  }, [checklist, showAllItems])

  const applicationTasks = useMemo(() => {
    if (user && filteredChecklist) {
      return parseTaskList(filteredChecklist, user, team, true)
    }
    return []
  }, [filteredChecklist, user, team, parseTaskList])

  const roadPreparationTasks = useMemo(() => {
    if (user && roadChecklist) {
      return parseTaskList(roadChecklist, user, team, false)
    }
    return []
  }, [roadChecklist, user, team, parseTaskList])

  if (isLoading) {
    return <InlineSpinner />
  }
  const showRoadChecklist =
    shouldShowOnboardingChecklist(filteredChecklist, roadChecklist) || forceShowOnboardingTasks

  function getTasks() {
    if (isConsole()) return applicationTasks
    if (showRoadChecklist) return roadPreparationTasks
    return applicationTasks
  }

  const tasks = getTasks()

  function renderHeader() {
    if (isConsole()) {
      return (
        <FlexBox justifyContent="space-between" alignItems="center" mb={-2}>
          <TextV2 type={TYPOGRAPHY.header3} className="text-left">
            Application tasks
          </TextV2>
        </FlexBox>
      )
    }

    if (!isCurrentUser) {
      return (
        <FlexBox isColumn justifyContent="center" alignItems="center" mb={8}>
          <TextV2 type={TYPOGRAPHY.header1}>
            Your {user.isTeamDriver ? 'driver' : ''} {user.isTeamAdmin ? 'admin' : ''}
          </TextV2>

          <TextV2 type={TYPOGRAPHY.label2} className="text-dark-gray">
            {user.firstName} {user.lastName}
          </TextV2>
        </FlexBox>
      )
    }

    if (onboardingStatus?.header) {
      return (
        <FlexBox isColumn justifyContent="center" alignItems="center" mb={8}>
          <TextV2 type={TYPOGRAPHY.header1}>{onboardingStatus?.header}</TextV2>

          {onboardingStatus?.subheader && (
            <FlexBox mt={1}>
              <TextV2 type={TYPOGRAPHY.label2} className="text-dark-gray">
                {onboardingStatus?.subheader}
              </TextV2>
            </FlexBox>
          )}

          <Box mt={5}></Box>
        </FlexBox>
      )
    }

    if (isCreditOnly) {
      return (
        <TextV2 type={TYPOGRAPHY.header1} className="mt-0">
          {isCurrentUser ? 'Your' : `${user.firstName || 'Their'}'s`}{' '}
          {isCreditOnly ? 'credit application is in progress' : 'onboarding is in progress'}.
        </TextV2>
      )
    }

    if (showRoadChecklist) {
      if (!user?.isTeamAdmin) {
        return (
          <TextV2 type={TYPOGRAPHY.header1} className="mt-0">
            Complete {isCurrentUser ? 'your' : ''} onboarding tasks
          </TextV2>
        )
      }

      return (
        <TextV2 type={TYPOGRAPHY.header1} className="mt-0">
          Finish setting up your fleet
        </TextV2>
      )
    }

    return (
      <TextV2 type={TYPOGRAPHY.header1} className="mt-0">
        Apply to drive with CloudTrucks
      </TextV2>
    )
  }

  function renderRoadPreparationInConsole() {
    if (!isConsole() || !roadPreparationTasks?.length) return null
    return (
      <>
        <TextV2 type={TYPOGRAPHY.header3} className="mt-8">
          Onboarding tasks
        </TextV2>
        <TaskList tasks={roadPreparationTasks} isReadOnly={false} user={user} />
      </>
    )
  }

  if (
    !skipApplicationSummary &&
    user.isTeamDriver &&
    checklist[0]?.status === TaskStatusEnum.NOT_STARTED &&
    renderApplicationSummary &&
    isCurrentUser
  ) {
    return renderApplicationSummary()
  }

  if (
    !skipWelcomeBack &&
    checklist[0]?.status !== TaskStatusEnum.NOT_STARTED &&
    allChecklistItems
      .filter((c) => !NON_REQUIRED_TASKS.includes(c.name))
      .some((c) => [TaskStatusEnum.NOT_STARTED, TaskStatusEnum.ERROR].includes(c.status)) &&
    renderWelcomeBack &&
    isCurrentUser
  ) {
    return renderWelcomeBack()
  }

  return (
    <>
      <div className="text-center">
        {renderHeader()}
        <Box m={isConsole() ? 4 : 5} />
      </div>

      <TaskList tasks={tasks} isReadOnly={!isCurrentUser && !isConsole()} user={user} />
      {renderRoadPreparationInConsole()}

      {children?.(tasks, applicationTasks, roadPreparationTasks)}
      {isConsole() && checklist?.length !== filteredChecklist?.length ? (
        <TextV2
          type={TYPOGRAPHY.paragraph3}
          onClick={() => setShowAllItems(!showAllItems)}
          className="mt-4 cursor-pointer text-left"
        >
          {showAllItems ? 'Hide' : 'Show'} all items
        </TextV2>
      ) : null}
    </>
  )
}
